import React, { useRef, useMemo } from 'react';
import { Modal, Button, FormGroup, FormControl, ControlLabel, Row, Col } from 'react-bootstrap';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';

import Spinner from '../../../../../components/Loaders/Spinner';
import Alert from '../../../../../components/Alert';

const EditPaymentModal = ({ error, open, item, access, isLoading, isLoadingSubmit, onDeactivate, onSubmit, onHide }) => {
  const formNode = useRef(null);

  const formItems = useMemo(() => {
    const fields = [];
    if (item?.location?.length === 1) {
      fields.push({
        type: 'textarea',
        label: 'Code snippet',
        code: 'snippet',
        name: `code[${item?.location?.[0]}]`,
        value: item.code?.[item?.location?.[0]] || '',
      });
    }

    if (item?.location?.length > 1) {
      for (let index = 0; index < item.location.length; index++) {
        fields.push({
          type: 'textarea',
          label:
            item.location[index] === 'end_header'
              ? 'Code snippet for <head>'
              : item.location[index] === 'end_body'
                ? 'Code snippet for <body>'
                : null,
          code: item.location[index],
          name: `code[${item.location[index]}]`,
          value: item.code?.[item.location[index]] || '',
        });
      }
    }

    if (!isEmpty(item?.settings)) {
      for (const key in item?.settings) {
        if (Object.hasOwn(item?.settings, key)) {
          const setting = item?.settings?.[key];
          if (setting?.type === 'select' && !isEmpty(setting?.options)) {
            fields.push({
              ...setting,
              name: `settings[${setting?.name}]`,
              options: Object.entries(setting?.options)?.map(([key, value]) => ({ label: value, value: key })),
            });
          } else {
            fields.push({ ...setting, name: `settings[${setting?.name}]` });
          }
        }
      }
    }

    fields.push({
      type: 'select',
      label: 'Visibility',
      code: 'visibility',
      name: 'visibility',
      value: String(item?.visibility),
      options: [
        {
          value: '0',
          label: 'All',
        },
        {
          value: '1',
          label: 'Public',
        },
        {
          value: '2',
          label: 'Internal',
        },
      ],
    });

    if (item?.files?.length) {
      for (let index = 0; index < item.files.length; index++) {
        fields.push({ type: 'files', ...item.files[index] });
      }
    }

    return fields;
  }, [item]);

  const onSubmitForm = (event) => {
    event?.preventDefault();
    const current = formNode?.current;
    const data = new FormData(current);
    onSubmit?.(item?.mode, data);
  };

  return (
    <Modal show={open} onHide={onHide} backdrop="static" className="modal-md-custom">
      <form ref={formNode} onSubmit={onSubmitForm}>
        <Modal.Header closeButton>
          <Modal.Title>{item?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert text={error} />}
          {isLoading ? (
            <div className="modal-loader-small" />
          ) : (
            <>
              {formItems?.map((item, index) => {
                switch (item.type) {
                case 'input':
                  return (
                    <FormGroup key={item.code}>
                      <ControlLabel htmlFor={item.code}>{item.label}</ControlLabel>
                      <FormControl
                        id={item.code}
                        type={item.input_type || 'text'}
                        name={item.name}
                        defaultValue={item.value}
                        autoFocus={index === 0}
                      />
                      {item?.help && <small className="help-block">{item?.help}</small>}
                    </FormGroup>
                  );

                case 'textarea':
                  return (
                    <FormGroup key={item.code}>
                      <ControlLabel htmlFor={item.code}>{item.label}</ControlLabel>
                      <FormControl
                        id={item.code}
                        rows={7}
                        componentClass="textarea"
                        name={item.name}
                        defaultValue={item.value}
                        autoFocus={index === 0}
                      />
                    </FormGroup>
                  );

                case 'select':
                  return (
                    <FormGroup key={item.code}>
                      <ControlLabel htmlFor={item.code}>{item.label}</ControlLabel>
                      <FormControl
                        componentClass="select"
                        id={item.code}
                        name={item.name}
                        defaultValue={item.value || item.default_value}
                        autoFocus={index === 0}
                      >
                        {item?.options?.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </FormControl>
                    </FormGroup>
                  );

                case 'files':
                  return (
                    <Row key={item.file_name}>
                      <Col md={10}>
                        <FormGroup>
                          <ControlLabel htmlFor={item.file_name}>{item.file_name}</ControlLabel>
                          {' '}
                          {item.uploaded && (
                            <a href={`/${item.file_name}`} target="_blank" rel="noreferrer">
                              <span className="fal fa-external-link" />
                            </a>
                          )}
                          <input type="hidden" name="files[]" value={item.file_name} />
                          <FormControl id={item.file_name} type="file" name={item.file_name} accept={item.accept} />
                        </FormGroup>
                      </Col>
                    </Row>
                  );

                default:
                  return null;
                }
              })}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="custom__modal-footer">
          <Button bsStyle="primary" type="submit" disabled={isLoadingSubmit || isLoading} onClick={onSubmitForm}>
            {isLoadingSubmit && <Spinner />}
            <span>Save changes</span>
          </Button>
          <Button
            onClick={() => {
              onHide();
            }}
          >
            Cancel
          </Button>
          {item?.mode === 'edit' && access?.integrations_deactivate ? (
            <div className="pull-right">
              <Button disabled={isLoadingSubmit || isLoading} bsStyle="link" className="modal-delete-btn" onClick={onDeactivate}>
                Deactivate
              </Button>
            </div>
          ) : null}
        </Modal.Footer>
      </form>
    </Modal>
  );
};

EditPaymentModal.propTypes = {
  error: PropTypes.string,
  open: PropTypes.bool,
  item: PropTypes.object,
  access: PropTypes.object,
  isLoading: PropTypes.bool,
  isLoadingSubmit: PropTypes.bool,
  onDeactivate: PropTypes.func,
  onHide: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default EditPaymentModal;
